import type {
  GetListResponseOfSelectOption,
  GetListResponseOfTaskAssignmentSimpleResponse,
  GetTaskAssignmentListRequest,
  SelectOption,
  TaskAssignmentResponse,
  TaskBlendedResponse,
  TaskDefinitionRequest,
} from 'src/api/ProactApiClient';
import {
  TaskAssignmentStateEnum,
  TaskDefinitionStateEnum,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {pendingFormAssignmentIds} from 'src/app/forms/offline/pendingFormAssignments';
import {isEffectivelyOffline} from 'src/offline/connectivity';
import {offlineDb} from 'src/offline/db';
import {offlineQuery, readSnapshotData} from 'src/offline/offlineQuery';
import {PENDING_STATUSES} from 'src/offline/outbox';
import {inProjectScope, projectScopeId} from 'src/offline/projectScope';
import type {OutboxItem} from 'src/offline/types';
import {store} from 'src/redux/store';
import {ApiEndpoints} from 'src/shared/constants/apiEndpoints';
import type {IStateMachine} from 'src/types/StateMachine';
import type {PendingSync} from 'src/offline/pendingSync';
import {TASK_OUTBOX_KINDS} from './outboxKinds';
import type {CachedAssignmentRow} from './tasksBundle';
import {
  taskActionKey,
  taskAssignmentKey,
  tasksAssignmentsListKey,
  tasksAssignmentStateMachineKey,
  tasksComplianceStandardsKey,
  tasksDefinitionStateMachineKey,
  tasksProjectsKey,
  TASKS_CONTEXT_KEY,
} from './snapshotKeys';

// The tasks session is always active online, so reads only divert while offline.
const tasksContext = {
  contextKey: () => TASKS_CONTEXT_KEY,
  shouldDivert: () => isEffectivelyOffline(),
};

export type PendingAssignmentRow = PendingSync<CachedAssignmentRow>;

// A queued Action create has no assignment yet; it shows as its own pending row
// until the create publishes and the real assignment arrives.
const toPendingActionRow = (item: OutboxItem): PendingAssignmentRow => {
  const payload = item.payload as TaskDefinitionRequest;
  const user = store.getState().session.user;
  return {
    id: -item.seq,
    taskId: -item.seq,
    sourceEntityId: 0,
    assignedToUserId: user?.id ?? 0,
    state: TaskAssignmentStateEnum.Assigned,
    createdAt: new Date(item.createdAt),
    updatedAt: new Date(item.createdAt),
    dueAt: payload.dueAt,
    pendingSync: true,
    taskDefinition: {
      id: -item.seq,
      subject: payload.subject,
      taskType: TaskTypeEnum.Actions,
      state: TaskDefinitionStateEnum.Draft,
      dueAt: payload.dueAt,
      projectId: payload.projectId,
    },
    assignedToUser: {id: user?.id ?? 0, name: user?.name ?? 'You'},
  } as unknown as PendingAssignmentRow;
};

const matchesRequest = (
  row: PendingAssignmentRow,
  params: GetTaskAssignmentListRequest
): boolean => {
  const scopeId = projectScopeId();
  const search = params.search?.trim().toLowerCase();
  return (
    (params.taskType === undefined ||
      row.taskDefinition?.taskType === params.taskType) &&
    ((params.assignmentStates?.length ?? 0) === 0 ||
      params.assignmentStates!.includes(row.state)) &&
    (scopeId === undefined ||
      inProjectScope(row.taskDefinition?.projectId, scopeId)) &&
    (!search ||
      (row.taskDefinition?.subject ?? '').toLowerCase().includes(search))
  );
};

// Only the signed-in user's own open work is bundled; every other request shape
// (another user, a single task, direct reports) misses to the network.
const isBundledListRequest = (params: GetTaskAssignmentListRequest): boolean =>
  params.assignedToUserId !== undefined &&
  params.assignedToUserId === store.getState().session.user?.id &&
  params.taskId === undefined &&
  params.directReportsOnly !== true;

export const taskAssignmentsListQueryFn = (
  call: (
    params: GetTaskAssignmentListRequest
  ) => Promise<GetListResponseOfTaskAssignmentSimpleResponse>
) =>
  offlineQuery<
    GetTaskAssignmentListRequest,
    GetListResponseOfTaskAssignmentSimpleResponse
  >({
    ...tasksContext,
    read: async (session, params) => {
      if (!isBundledListRequest(params)) return undefined;
      const rows = await readSnapshotData<PendingAssignmentRow[]>(
        session.id,
        tasksAssignmentsListKey
      );
      if (!rows) return undefined;

      const items = await offlineDb.outbox
        .where('sessionId')
        .equals(session.id)
        .toArray();
      const pendingCreates = items.filter(
        item =>
          item.kind === TASK_OUTBOX_KINDS.create &&
          PENDING_STATUSES.includes(item.status)
      );
      const queuedFormAssignments = await pendingFormAssignmentIds();

      const all = [
        ...pendingCreates.map(toPendingActionRow),
        ...rows.map(row =>
          queuedFormAssignments.has(row.id) ? {...row, pendingSync: true} : row
        ),
      ].filter(row => matchesRequest(row, params));

      const start = params.start ?? 0;
      const length = params.length ?? all.length;
      return {
        data: all.slice(start, start + length),
        totalRecords: all.length,
        filteredRecords: all.length,
      };
    },
    call,
  });

export const taskAssignmentDetailsQueryFn = (
  call: (id: number) => Promise<TaskAssignmentResponse>
) =>
  offlineQuery<number, TaskAssignmentResponse>({
    ...tasksContext,
    read: (session, id) => readSnapshotData(session.id, taskAssignmentKey(id)),
    call,
  });

export const actionQueryFn = (
  call: (id: number) => Promise<TaskBlendedResponse>
) =>
  offlineQuery<number, TaskBlendedResponse>({
    ...tasksContext,
    read: (session, id) => readSnapshotData(session.id, taskActionKey(id)),
    call,
  });

const TASK_MACHINE_KEYS: Record<string, string> = {
  [ApiEndpoints.getTaskAssignmentStateMachine]: tasksAssignmentStateMachineKey,
  [ApiEndpoints.getTaskDefinitionStateMachine]: tasksDefinitionStateMachineKey,
};

export const isTaskStateMachineUrl = (url: string): boolean =>
  TASK_MACHINE_KEYS[url] !== undefined;

// Serves the cached task machines offline so the transition bars still render.
export const taskStateMachineQueryFn = (
  call: (url: string) => Promise<IStateMachine>
) =>
  offlineQuery<string, IStateMachine>({
    contextKey: url =>
      isTaskStateMachineUrl(url) ? TASKS_CONTEXT_KEY : undefined,
    shouldDivert: tasksContext.shouldDivert,
    read: (session, url) => readSnapshotData(session.id, TASK_MACHINE_KEYS[url]),
    call,
  });

const optionsQueryFn = <Arg>(key: string, call: (arg: Arg) => Promise<GetListResponseOfSelectOption>) =>
  offlineQuery<Arg, GetListResponseOfSelectOption>({
    ...tasksContext,
    read: async session => {
      const options = await readSnapshotData<SelectOption[]>(session.id, key);
      if (!options) return undefined;
      return {
        data: options,
        totalRecords: options.length,
        filteredRecords: options.length,
      };
    },
    call,
  });

export const complianceStandardSelectOptionsQueryFn = <Arg>(
  call: (arg: Arg) => Promise<GetListResponseOfSelectOption>
) => optionsQueryFn<Arg>(tasksComplianceStandardsKey, call);

export const projectSelectOptionsQueryFn = <Arg>(
  call: (arg: Arg) => Promise<GetListResponseOfSelectOption>
) => optionsQueryFn<Arg>(tasksProjectsKey, call);
